import { useState } from 'react';
import { RotateCcw, AlertTriangle, ArrowLeft, ShieldCheck, Printer, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWirelessPrinter } from '@/features/hardware/hooks/useWirelessPrinter';
import { compileTejVoucher } from '@/features/hardware/utils/compileTejVoucher';

interface ReturnItemPayload {
  productId: string;
  variantId?: string | null;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  reason: string;
  totalAmount: number;
  condition: 'SELLABLE' | 'DAMAGED';
}

interface ReturnProcessingProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

export function ReturnProcessing({ onSuccess }: ReturnProcessingProps) {
  const { user } = useAuth();
  const { isConnected: isPrinterConnected, isPrinting: isPrinterPrinting, sendPrintJob, connectPrinter } = useWirelessPrinter();

  const [searchQuery, setSearchQuery] = useState('');
  const [invoice, setInvoice] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Selected item states
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [returnQuantities, setReturnQuantities] = useState<Record<string, number>>({});
  const [itemConditions, setItemConditions] = useState<Record<string, 'SELLABLE' | 'DAMAGED'>>({});
  const [returnReasons, setReturnReasons] = useState<Record<string, string>>({});

  // Generated token state
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherData, setVoucherData] = useState<any | null>(null);
  const [refundMode, setRefundMode] = useState<'TOKEN' | 'CASH'>('TOKEN');

  const resetSelection = () => {
    setShowVoucherModal(false);
    setInvoice(null);
    setSearchQuery('');
    setSelectedItems({});
    setReturnQuantities({});
    setItemConditions({});
    setReturnReasons({});
    setRefundMode('TOKEN');
    onSuccess();
  };

  const isInvoiceFullyReturned = invoice && invoice.items.every((item: any) => {
    const returnedQty = item.returnedQuantity || 0;
    return returnedQty >= item.quantity;
  });

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearchError('');
    setInvoice(null);
    setLoading(true);

    try {
      const headers = { 'x-tenant-id': user?.tenantId };
      const res = await api.get('/billing/invoices', { headers });
      const found = (res.data.data || []).find(
        (inv: any) =>
          inv.id.toLowerCase() === searchQuery.trim().toLowerCase() ||
          inv.invoiceNumber.toLowerCase() === searchQuery.trim().toLowerCase()
      );

      if (!found) {
        setSearchError('Invoice not found in system repository.');
        return;
      }

      setInvoice(found);
      const qtys: Record<string, number> = {};
      const sel: Record<string, boolean> = {};
      const conds: Record<string, 'SELLABLE' | 'DAMAGED'> = {};
      const reasons: Record<string, string> = {};

      found.items.forEach((item: any) => {
        const key = item.id;
        const returnedQty = item.returnedQuantity || 0;
        const maxQty = item.quantity - returnedQty;
        qtys[key] = maxQty > 0 ? 1 : 0;
        sel[key] = false;
        conds[key] = 'SELLABLE';
        reasons[key] = 'Damaged Product / Fabric Flaw';
      });

      setReturnQuantities(qtys);
      setSelectedItems(sel);
      setItemConditions(conds);
      setReturnReasons(reasons);
    } catch (err) {
      setSearchError('Failed to search database.');
    } finally {
      setLoading(false);
    }
  };

  const handleQtyChange = (key: string, max: number, delta: number) => {
    setReturnQuantities(prev => {
      const current = prev[key] || 1;
      const next = Math.max(1, Math.min(max, current + delta));
      return { ...prev, [key]: next };
    });
  };

  const toggleSelect = (key: string) => {
    setSelectedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConditionChange = (key: string, condition: 'SELLABLE' | 'DAMAGED') => {
    setItemConditions(prev => ({ ...prev, [key]: condition }));
  };

  const handleReasonChange = (key: string, val: string) => {
    setReturnReasons(prev => ({ ...prev, [key]: val }));
  };

  const calculateReturnTotal = () => {
    if (!invoice) return 0;
    let total = 0;
    invoice.items.forEach((item: any) => {
      if (selectedItems[item.id]) {
        const qty = returnQuantities[item.id] || 1;
        // Total refund value per item includes item's proportional unitPrice and its original GST tax
        const lineTotal = item.unitPrice * qty;
        const lineGst = lineTotal * (item.gstRate / 100);
        total += lineTotal + lineGst;
      }
    });
    return total;
  };

  const handleSubmitReturn = async () => {
    if (!invoice || !user?.tenantId) return;
    setSubmitError('');

    const itemsToSubmit: ReturnItemPayload[] = [];
    invoice.items.forEach((item: any) => {
      if (selectedItems[item.id]) {
        const qty = returnQuantities[item.id] || 1;
        const baseCost = item.unitPrice * qty;
        const gstCost = baseCost * (item.gstRate / 100);

        itemsToSubmit.push({
          productId: item.productId,
          variantId: item.variantId || null,
          productName: item.productName || item.name,
          sku: item.sku || '',
          quantity: qty,
          unitPrice: item.unitPrice,
          reason: returnReasons[item.id] || 'Not specified',
          totalAmount: baseCost + gstCost,
          condition: itemConditions[item.id] || 'SELLABLE'
        });
      }
    });

    if (itemsToSubmit.length === 0) {
      setSubmitError('Please select at least one item to return.');
      return;
    }

    const totalRefund = calculateReturnTotal();

    const payload = {
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      customerName: invoice.customerName,
      customerId: invoice.customerId || null,
      totalRefundAmount: totalRefund,
      items: itemsToSubmit,
      notes: refundMode === 'CASH'
        ? `Return cash refund. Refunded cash amount: ₹${totalRefund.toFixed(2)}`
        : `Return exchange token credit. Issued credit amount: ₹${totalRefund.toFixed(2)}`,
      restock: itemsToSubmit.some(i => i.condition === 'SELLABLE'), // true if any items are sellable
      refundMode
    };

    try {
      const headers = { 'x-tenant-id': user.tenantId };
      const res = await api.post('/returns/issue-token', payload, { headers });
      if (res.data.success) {
        setVoucherData(res.data.data);
        setShowVoucherModal(true);
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to process return and issue exchange credit.');
    }
  };

  const handlePrintVoucher = async () => {
    if (!voucherData || !voucherData.exchangeToken) return;
    
    try {
      if (!isPrinterConnected) {
        const connected = await connectPrinter();
        if (!connected) return;
      }
      
      const payload = {
        storeName: user?.tenantName || 'Katirtek Store',
        tokenCode: voucherData.exchangeToken.tokenCode,
        creditAmount: voucherData.exchangeToken.creditAmount,
        customerName: invoice?.customerName || 'Walk-in Customer',
        expiryDate: new Date(voucherData.exchangeToken.expiryDate).toLocaleDateString()
      };

      const printBytes = compileTejVoucher(payload);
      await sendPrintJob(printBytes);
    } catch (err) {
      alert('Wireless voucher printing failed.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Return lookup component */}
      {!invoice ? (
        <div className="card max-w-xl mx-auto text-center p-8 space-y-6 border border-slate-200 bg-white rounded-xl shadow-sm">
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center justify-center gap-1.5">
              <RotateCcw size={16} className="text-emerald-600 animate-spin" />
              <span>Verify Invoice for Return Credit</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Enter the exact invoice receipt number to audit the garments and register credit vouchers.
            </p>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              required
              placeholder="e.g. INV-2026-0001"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-600"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 active:scale-95 transition-all shadow"
            >
              {loading ? 'Searching...' : 'Lookup Invoice'}
            </button>
          </form>

          {searchError && (
            <div className="text-[11px] text-red-500 font-bold flex items-center justify-center gap-1 bg-red-50 p-2 rounded border border-red-100">
              <AlertTriangle size={13} />
              <span>{searchError}</span>
            </div>
          )}
        </div>
      ) : (
        /* Return items matrix */
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <div className="card bg-white p-5 border border-slate-200 rounded-xl shadow-sm space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <button
                  onClick={() => setInvoice(null)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-bold flex items-center gap-1 cursor-pointer"
                >
                  <ArrowLeft size={14} />
                  <span>Choose Another Invoice</span>
                </button>
                <span className="text-[10px] font-mono text-slate-400 font-bold uppercase">
                  Invoice Ref: {invoice.invoiceNumber || invoice.id}
                </span>
              </div>

              {isInvoiceFullyReturned && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs font-bold text-rose-800 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-rose-600 animate-pulse shrink-0" />
                  <span>All garment items in this invoice have already been returned. No item is available to select.</span>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-155 text-slate-400 font-bold uppercase tracking-wider bg-slate-50">
                      <th className="py-2.5 px-3 w-12 text-center">S.No.</th>
                      <th className="py-2.5 px-3 w-10 text-center">Select</th>
                      <th className="py-2.5 px-2">Garment Item</th>
                      <th className="py-2.5 px-2 text-right">Price + GST</th>
                      <th className="py-2.5 px-2 text-center">Qty Purchased</th>
                      <th className="py-2.5 px-2 text-center">Qty to Return</th>
                      <th className="py-2.5 px-2">Condition</th>
                      <th className="py-2.5 px-3">Return Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invoice.items.map((item: any, idx: number) => {
                      const isChecked = selectedItems[item.id] || false;
                      const returnQty = returnQuantities[item.id] || 1;
                      const condition = itemConditions[item.id] || 'SELLABLE';
                      const reason = returnReasons[item.id] || 'Fabric Flaw';

                      const returnedQty = item.returnedQuantity || 0;
                      const maxQty = item.quantity - returnedQty;
                      const isAlreadyReturned = maxQty <= 0;

                      return (
                        <tr key={item.id} className={`hover:bg-slate-50/50 ${isChecked ? 'bg-emerald-50/10' : ''} ${isAlreadyReturned ? 'opacity-40 bg-slate-100/50' : ''}`}>
                          <td className="py-3 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                          <td className="py-3 px-3 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isAlreadyReturned}
                              onChange={() => toggleSelect(item.id)}
                              className="h-3.5 w-3.5 text-emerald-600 border-slate-200 rounded cursor-pointer"
                            />
                          </td>
                          <td className="py-3 px-2">
                            <span className={`font-bold text-slate-800 block ${isAlreadyReturned ? 'line-through text-slate-450' : ''}`}>{item.name}</span>
                            <span className="text-[9px] font-mono text-slate-400 mt-0.5 block">{item.sku}</span>
                            {returnedQty > 0 && (
                              <span className="text-[9px] font-bold text-red-655 bg-red-50 border border-red-150 px-1.5 py-0.5 rounded mt-1 inline-block">
                                Already Returned: {returnedQty} / {item.quantity}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-right font-mono font-semibold text-slate-700">
                            ₹{(item.unitPrice * (1 + item.gstRate / 100)).toFixed(2)}
                          </td>
                          <td className="py-3 px-2 text-center font-bold text-slate-700">{item.quantity}</td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                type="button"
                                disabled={!isChecked || isAlreadyReturned}
                                onClick={() => handleQtyChange(item.id, maxQty, -1)}
                                className="w-4 h-4 rounded border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-100 disabled:opacity-50 text-slate-600"
                              >
                                -
                              </button>
                              <span className="w-5 text-center font-bold font-mono text-slate-800">{isAlreadyReturned ? 0 : returnQty}</span>
                              <button
                                type="button"
                                disabled={!isChecked || isAlreadyReturned}
                                onClick={() => handleQtyChange(item.id, maxQty, 1)}
                                className="w-4 h-4 rounded border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-100 disabled:opacity-50 text-slate-600"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <select
                              disabled={!isChecked || isAlreadyReturned}
                              value={condition}
                              onChange={(e) => handleConditionChange(item.id, e.target.value as any)}
                              className="px-2 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 bg-white text-slate-700 font-semibold"
                            >
                              <option value="SELLABLE">Sellable Stock</option>
                              <option value="DAMAGED">Damaged Bin</option>
                            </select>
                          </td>
                          <td className="py-3 px-3">
                            <input
                              type="text"
                              disabled={!isChecked || isAlreadyReturned}
                              value={reason}
                              onChange={(e) => handleReasonChange(item.id, e.target.value)}
                              className="w-full px-2 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:border-emerald-600 bg-white text-slate-700 font-medium"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Refund credit assessment panel */}
          <div className="space-y-6">
            <div className="card bg-slate-50 border border-slate-200 p-5 rounded-xl shadow-sm space-y-4">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-2 flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-emerald-600" />
                <span>Return Assessment Ledger</span>
              </h3>

              <div className="space-y-4 text-xs font-medium">
                {/* Choice Selection */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Refund Method</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRefundMode('TOKEN')}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        refundMode === 'TOKEN'
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                          : 'bg-white border-slate-250 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      Store Credit Token
                    </button>
                    <button
                      type="button"
                      onClick={() => setRefundMode('CASH')}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        refundMode === 'CASH'
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                          : 'bg-white border-slate-250 hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      Cash Refund
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center text-slate-600 pt-2 border-t border-slate-100">
                  <span>Selected Return Value</span>
                  <span className="font-mono font-bold text-lg text-emerald-800">
                    ₹{calculateReturnTotal().toFixed(2)}
                  </span>
                </div>

                {submitError && (
                  <p className="text-[10px] text-red-550 font-bold bg-red-50 p-2 rounded border border-red-100">{submitError}</p>
                )}

                <button
                  onClick={handleSubmitReturn}
                  disabled={isInvoiceFullyReturned || calculateReturnTotal() === 0}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded shadow-sm border border-emerald-500 active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-350 disabled:border-slate-350"
                >
                  <RotateCcw size={14} />
                  <span>{isInvoiceFullyReturned ? 'All Items Returned' : refundMode === 'CASH' ? 'Process Cash Refund' : 'Issue Return Exchange Token'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voucher Display Modal */}
      {showVoucherModal && voucherData && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden border border-slate-100 animate-scaleUp">
             <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {voucherData.exchangeToken.tokenCode.startsWith('CSH-') ? 'Cash Refund Slip' : 'Exchange Voucher Slip'}
              </span>
              <button
                onClick={resetSelection}
                className="text-slate-400 hover:text-slate-650 cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-6 bg-slate-100 flex flex-col items-center">
              {/* Receipt mockup */}
              <div
                className="bg-white p-5 border border-slate-200 w-full max-w-[270px] font-mono text-[10px] text-slate-900 shadow-2xl relative"
                style={{
                  borderBottom: '4px dashed #cbd5e1'
                }}
              >
                <div className="text-center border-b border-dashed border-slate-300 pb-3 mb-3">
                  <h4 className="font-bold text-xs uppercase">{user?.tenantName || 'TriaPOS Store'}</h4>
                  <p className="text-[8px] text-slate-400 mt-0.5">
                    {voucherData.exchangeToken.tokenCode.startsWith('CSH-') ? 'CASH REFUND RECEIPT' : 'EXCHANGE CREDIT VOUCHER'}
                  </p>
                </div>

                <div className="space-y-2 text-center py-2 bg-slate-50 border border-slate-150 rounded mb-4">
                  <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest block">
                    {voucherData.exchangeToken.tokenCode.startsWith('CSH-') ? 'Refunded Cash Amount' : 'Token Credit Amount'}
                  </span>
                  <span className="text-lg font-black text-emerald-800">₹{voucherData.exchangeToken.creditAmount.toFixed(2)}</span>
                </div>

                <div className="space-y-1 text-slate-700 border-t border-b border-dashed border-slate-200 py-3 my-2">
                  <div className="flex justify-between">
                    <span>{voucherData.exchangeToken.tokenCode.startsWith('CSH-') ? 'REFUND CODE:' : 'TOKEN CODE:'}</span>
                    <span className="font-bold text-slate-900">{voucherData.exchangeToken.tokenCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CUSTOMER:</span>
                    <span className="font-semibold text-slate-900 truncate max-w-[120px]">{invoice?.customerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>EXPIRY DATE:</span>
                    <span>{new Date(voucherData.exchangeToken.expiryDate).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Simulated barcode */}
                <div className="flex flex-col items-center justify-center pt-2">
                  <div className="flex items-end h-8 justify-center overflow-hidden">
                    {[...Array(16)].map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx % 3 === 0 ? '3px' : '1px',
                          marginRight: '2px'
                        }}
                        className="bg-slate-800 h-6 shrink-0"
                      />
                    ))}
                  </div>
                  <span className="text-[7px] tracking-wider text-slate-500 mt-1">{voucherData.exchangeToken.tokenCode}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
              <button
                onClick={resetSelection}
                className="flex-1 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm cursor-pointer"
              >
                Close View
              </button>
              <button
                onClick={handlePrintVoucher}
                disabled={isPrinterPrinting}
                className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow border border-emerald-500 active:scale-[0.98] transition-all flex items-center justify-center gap-1 cursor-pointer"
              >
                <Printer size={13} />
                <span>{isPrinterPrinting ? 'Printing...' : 'Print Voucher'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
